import { useEffect, useMemo, useState } from 'react';
import type { ModerationProfileBase } from '../types/moderationProfile';
import type { ModerationQueueTab } from '../components/ModerationQueueSection';

type SelectionMap = Record<ModerationQueueTab, string | null>;

const emptySelection = (): SelectionMap => ({
  rejected: null,
  pending: null,
  approved: null,
});

export const useQueueProfileSelection = (
  queueTab: ModerationQueueTab,
  pending: ModerationProfileBase[],
  approved: ModerationProfileBase[],
  rejected: ModerationProfileBase[]
) => {
  const [selectedByQueue, setSelectedByQueue] = useState<SelectionMap>(emptySelection);

  const profilesForTab = useMemo(() => {
    if (queueTab === 'pending') return pending;
    if (queueTab === 'approved') return approved;
    return rejected;
  }, [queueTab, pending, approved, rejected]);

  useEffect(() => {
    setSelectedByQueue((prev) => {
      const current = prev[queueTab];
      if (!profilesForTab.length) {
        if (current === null) return prev;
        return { ...prev, [queueTab]: null };
      }
      if (current && profilesForTab.some((p) => p.id === current)) {
        return prev;
      }
      return { ...prev, [queueTab]: profilesForTab[0].id };
    });
  }, [queueTab, profilesForTab]);

  const selectedProfileId = selectedByQueue[queueTab];

  const selectProfile = (profileId: string) => {
    setSelectedByQueue((prev) => ({ ...prev, [queueTab]: profileId }));
  };

  return { profilesForTab, selectedProfileId, selectProfile };
};
